import { useRef, useState, useEffect } from 'react';

// Decorative waveform — symmetric mountain envelope, 40 values
const WAVEFORM = [
  0.2, 0.32, 0.48, 0.6, 0.52, 0.72, 0.58, 0.84, 0.68, 0.9, 0.76, 1.0, 0.82, 0.96, 0.88, 0.92, 0.78,
  0.86, 0.72, 0.8, 0.8, 0.72, 0.86, 0.78, 0.92, 0.88, 0.96, 0.82, 1.0, 0.76, 0.9, 0.68, 0.84, 0.58,
  0.72, 0.52, 0.6, 0.48, 0.32, 0.2,
];

export const WAVEFORM_CANVAS_H = 28; // waveform bar height in CSS px
const BAR_W = 3;
const BAR_GAP = 2;

export interface UseWaveformCanvasReturn {
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  containerRef: React.RefObject<HTMLDivElement | null>;
  playedProbeRef: React.RefObject<HTMLDivElement | null>;
  unplayedProbeRef: React.RefObject<HTMLDivElement | null>;
}

export function useWaveformCanvas(progress: number, colorMode: string): UseWaveformCanvasReturn {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Invisible probes let Chakra resolve semantic token colors to real RGB values that canvas can use
  const playedProbeRef = useRef<HTMLDivElement>(null);
  const unplayedProbeRef = useRef<HTMLDivElement>(null);
  const [canvasWidth, setCanvasWidth] = useState(0);

  // Track container width so the canvas stays responsive
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      setCanvasWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Redraw canvas on every progress or width change.
  // Canvas renders all bars in one paint pass — far faster than 40 individual DOM elements.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || canvasWidth <= 0) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(canvasWidth * dpr);
    canvas.height = Math.round(WAVEFORM_CANVAS_H * dpr);

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, canvasWidth, WAVEFORM_CANVAS_H);

    // Read actual colors from the probe elements; Chakra has already resolved the semantic tokens.
    // Probes are always mounted; fallback to transparent (unreachable in practice).
    const playedColor = playedProbeRef.current
      ? getComputedStyle(playedProbeRef.current).backgroundColor
      : 'transparent';
    const unplayedColor = unplayedProbeRef.current
      ? getComputedStyle(unplayedProbeRef.current).backgroundColor
      : 'transparent';

    const step = BAR_W + BAR_GAP;
    const n = Math.floor(canvasWidth / step);

    for (let i = 0; i < n; i++) {
      const fraction = WAVEFORM[Math.round((i / Math.max(1, n - 1)) * (WAVEFORM.length - 1))];
      const h = Math.max(2, fraction * WAVEFORM_CANVAS_H);
      const x = i * step;
      const y = (WAVEFORM_CANVAS_H - h) / 2;
      ctx.fillStyle = i / n < progress ? playedColor : unplayedColor;
      if (typeof ctx.roundRect === 'function') {
        ctx.beginPath();
        ctx.roundRect(x, y, BAR_W, h, 1.5);
        ctx.fill();
      } else {
        ctx.fillRect(x, y, BAR_W, h);
      }
    }
  }, [canvasWidth, progress, colorMode]);

  return { canvasRef, containerRef, playedProbeRef, unplayedProbeRef };
}
